import { PropsWithChildren, useCallback, useMemo } from "react";
import { useGenericContext, useStoredState } from "../../hooks";
import { ITerritoryContext, TerritoryContext } from ".";
import { useTerritoryQuery } from "../../queries";
import { Office } from "../../models";

export const TerritoryProvider = (props: PropsWithChildren) => {
    const {
        listQuery: { data: _territories },
    } = useTerritoryQuery();

    const territories = useMemo(() => _territories ?? [], [_territories]);

    const offices = useMemo(() => {
        return territories
            .reduce<Office[]>((acc, territory) => acc.concat(territory.offices), [])
            .filter((office, index, self) => self.findIndex((r) => r.id === office.id) === index);
    }, [territories]);

    const defaultTerritoryIds = useMemo(() => territories.map((t) => t.id), [territories]);
    const defaultOfficeIds = useMemo(() => offices.map((office) => office.id), [offices]);

    const [selectedTerritoryIds, setSelectedTerritoryIds] = useStoredState<number[]>(
        "selectedTerritoryIds",
        defaultTerritoryIds
    );
    const [selectedOfficeIds, setSelectedOfficeIds] = useStoredState<number[]>("selectedOfficeIds", defaultOfficeIds);

    const selectedTerritories = useMemo(
        () => territories.filter((t) => selectedTerritoryIds.includes(t.id)),
        [selectedTerritoryIds, territories]
    );

    const selectedOffices = useMemo(
        () => offices.filter((r) => selectedOfficeIds.includes(r.id)),
        [selectedOfficeIds, offices]
    );

    const allOfficesSelected = useMemo(() => selectedOffices.length === offices.length, [offices, selectedOffices]);

    const handleSelectAllOffices = useCallback(() => {
        const updatedTerritories = new Set(selectedTerritoryIds);
        const updatedOffices = new Set(selectedOfficeIds);

        if (!allOfficesSelected) {
            territories.forEach((territory) => {
                updatedTerritories.add(territory.id);
                territory.offices.forEach((office) => {
                    updatedOffices.add(office.id);
                });
            });
        } else {
            updatedTerritories.clear();
            updatedOffices.clear();
        }

        setSelectedTerritoryIds(Array.from(updatedTerritories));
        setSelectedOfficeIds(Array.from(updatedOffices));
    }, [
        allOfficesSelected,
        selectedOfficeIds,
        selectedTerritoryIds,
        setSelectedOfficeIds,
        setSelectedTerritoryIds,
        territories,
    ]);

    const toggleTerritory = useCallback(
        (territoryId: number) => {
            const updatedTerritories = new Set(selectedTerritoryIds);
            const updatedOffices = new Set(selectedOfficeIds);

            if (updatedTerritories.has(territoryId)) {
                updatedTerritories.delete(territoryId);
                territories
                    .find((t) => t.id === territoryId)
                    ?.offices.forEach((office) => {
                        updatedOffices.delete(office.id);
                    });
            } else {
                updatedTerritories.add(territoryId);
                territories
                    .find((t) => t.id === territoryId)
                    ?.offices.forEach((office) => {
                        updatedOffices.add(office.id);
                    });
            }

            setSelectedTerritoryIds(Array.from(updatedTerritories));
            setSelectedOfficeIds(Array.from(updatedOffices));
        },
        [selectedOfficeIds, selectedTerritoryIds, setSelectedOfficeIds, setSelectedTerritoryIds, territories]
    );

    const toggleOffice = useCallback(
        (officeId: number, territoryId: number) => {
            const updatedOffices = new Set(selectedOfficeIds);
            const updatedTerritories = new Set(selectedTerritoryIds);

            if (updatedOffices.has(officeId)) {
                updatedOffices.delete(officeId);

                const officeCount = territories
                    .find((t) => t.id === territoryId)
                    ?.offices.filter((office) => updatedOffices.has(office.id)).length;

                if (officeCount === 0) {
                    updatedTerritories.delete(territoryId);
                }
            } else {
                updatedOffices.add(officeId);
                updatedTerritories.add(territoryId);
            }

            setSelectedOfficeIds(Array.from(updatedOffices));
            setSelectedTerritoryIds(Array.from(updatedTerritories));
        },
        [selectedOfficeIds, selectedTerritoryIds, setSelectedOfficeIds, setSelectedTerritoryIds, territories]
    );

    const value = useMemo<ITerritoryContext>(
        () => ({
            territories,
            offices,
            selectedTerritoryIds,
            selectedOfficeIds,
            setSelectedTerritoryIds,
            setSelectedOfficeIds,
            toggleTerritory,
            toggleOffice,
            selectedTerritories,
            selectedOffices,
            handleSelectAllOffices,
            allOfficesSelected,
        }),
        [
            territories,
            offices,
            selectedTerritoryIds,
            selectedOfficeIds,
            setSelectedTerritoryIds,
            setSelectedOfficeIds,
            toggleTerritory,
            toggleOffice,
            selectedTerritories,
            selectedOffices,
            handleSelectAllOffices,
            allOfficesSelected,
        ]
    );

    return <TerritoryContext.Provider value={value} {...props} />;
};
